import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { pagesAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const AdminPages = () => {
  const [pages, setPages] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    is_active: true,
    show_in_menu: true,
  });
  const { toast } = useToast();

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const response = await pagesAPI.getAll();
      setPages(response.data);
    } catch (error) {
      toast({ title: 'Hata', description: 'Sayfalar yüklenemedi', variant: 'destructive' });
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (title) => {
    setFormData({
      ...formData,
      title,
      slug: editingPage ? formData.slug : generateSlug(title),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await pagesAPI.update(editingPage._id, formData);
        toast({ title: 'Başarılı', description: 'Sayfa güncellendi' });
      } else {
        await pagesAPI.create(formData);
        toast({ title: 'Başarılı', description: 'Sayfa eklendi' });
      }

      setIsDialogOpen(false);
      resetForm();
      loadPages();
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.detail || 'Sayfa kaydedilemedi',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title,
      slug: page.slug,
      content: page.content,
      is_active: page.is_active,
      show_in_menu: page.show_in_menu,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu sayfayı silmek istediğinizden emin misiniz?')) {
      try {
        await pagesAPI.delete(id);
        toast({ title: 'Başarılı', description: 'Sayfa silindi' });
        loadPages();
      } catch (error) {
        toast({ title: 'Hata', description: 'Sayfa silinemedi', variant: 'destructive' });
      }
    }
  };

  const resetForm = () => {
    setEditingPage(null);
    setFormData({
      title: '',
      slug: '',
      content: '',
      is_active: true,
      show_in_menu: true,
    });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Sayfa Yönetimi</h1>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold"
          >
            <Plus size={20} className="mr-2" />
            Yeni Sayfa
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {pages.map((page) => (
            <Card key={page._id} className="bg-gray-900 border-gray-800 p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-white font-bold text-xl">{page.title}</h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs ${
                        page.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {page.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                    {page.show_in_menu && (
                      <span className="px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                        Menüde Göster
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm">
                    URL: <span className="text-[#FDB714]">/page/{page.slug}</span>
                  </p>
                  <p className="text-gray-500 text-sm mt-2 line-clamp-2">{page.content}</p>
                </div>
                <div className="flex gap-2">
                  <a href={`/page/${page.slug}`} target="_blank" rel="noopener noreferrer">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-gray-700 text-gray-300 hover:bg-gray-800"
                    >
                      <Eye size={16} />
                    </Button>
                  </a>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(page)}
                    className="border-gray-700 text-gray-300 hover:bg-gray-800"
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(page._id)}
                    className="border-red-700 text-red-500 hover:bg-red-900/20"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPage ? 'Sayfa Düzenle' : 'Yeni Sayfa Ekle'}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Sayfa Başlığı</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Örn: Gizlilik Politikası"
                  required
                />
              </div>

              <div>
                <Label>URL Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Örn: gizlilik-politikasi"
                  required
                />
                <p className="text-gray-500 text-xs mt-1">Sayfa URL'si: /page/{formData.slug}</p>
              </div>

              <div>
                <Label>İçerik</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="bg-gray-800 border-gray-700 min-h-[300px]"
                  placeholder="Sayfa içeriğini buraya yazın..."
                  required
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-start gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 mt-1"
                  />
                  <div>
                    <span className="font-semibold">Sayfa Aktif</span>
                    <p className="text-gray-400 text-sm">Sayfa erişime açık olacak. Kapalıysa kimse göremez.</p>
                  </div>
                </label>

                <label className="flex items-start gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_in_menu}
                    onChange={(e) => setFormData({ ...formData, show_in_menu: e.target.checked })}
                    className="w-4 h-4 mt-1"
                  />
                  <div>
                    <span className="font-semibold">Footer'da Göster</span>
                    <p className="text-gray-400 text-sm">Footer'daki "Kurumsal" bölümünde link olarak görünecek.</p>
                  </div>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold">
                  {editingPage ? 'Güncelle' : 'Ekle'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  İptal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminPages;
